import { useCallback, useEffect, useState } from "react";
import { CalendarX2 } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { formatDate } from "@/lib/format";

// Lote 98 — prazos que caem em sábado, domingo ou feriado nacional são prorrogados para o
// próximo dia útil (pedido do Robson, 22/10/2026). Daqui em diante o servidor já faz isso ao
// criar/editar; este aviso corrige os que já estavam cadastrados (com prévia e confirmação).
// Só aparece quando existe algum prazo sem baixa nessa situação.

const cap = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : "");

export default function PrazosDiaNaoUtilBanner({ onFixed }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    () => http.get("/prazos/dia-nao-util").then((r) => setData(r.data)).catch(() => {}),
    [],
  );
  useEffect(() => { load(); }, [load]);

  if (!data || !data.total) return null;

  const fix = async () => {
    setBusy(true);
    try {
      const r = await http.post("/prazos/dia-nao-util/corrigir");
      const n = r.data?.corrigidos || 0;
      toast.success(`${n} ${n === 1 ? "prazo prorrogado" : "prazos prorrogados"} para o próximo dia útil.`);
      setOpen(false);
      await load();
      if (onFixed) onFixed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível corrigir agora. Tente de novo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div data-testid="prazos-dia-nao-util-banner" className="mb-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 flex items-start gap-3">
        <CalendarX2 className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" strokeWidth={2} />
        <div className="min-w-0 flex-1 text-sm">
          <span className="font-semibold">
            {data.total} {data.total === 1 ? "prazo cai" : "prazos caem"} em sábado, domingo ou feriado.
          </span>{" "}
          <span className="text-amber-900/80">O correto é o próximo dia útil.</span>
        </div>
        <Button size="sm" data-testid="prazos-dia-nao-util-ver" className="shrink-0 bg-stone-900 hover:bg-stone-800 text-white" onClick={() => setOpen(true)}>
          Ver e corrigir
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="prazos-dia-nao-util-dialog">
          <DialogHeader>
            <DialogTitle>Prorrogar para o próximo dia útil</DialogTitle>
            <DialogDescription>
              Estes prazos ainda sem baixa caem em dia sem expediente. Ao confirmar, a data muda aqui e no Google Agenda.
            </DialogDescription>
          </DialogHeader>
          <div className="divide-y divide-stone-100">
            {data.items.map((it) => (
              <div key={it.pub_id} className="py-2.5" data-testid={`dia-nao-util-${it.pub_id}`}>
                <p className="text-sm font-semibold text-stone-900 break-words">{it.titulo}</p>
                <p className="text-xs text-stone-600">
                  {formatDate(it.de)} ({it.motivo}) <span aria-hidden="true">→</span> <b>{formatDate(it.para)}</b>
                </p>
              </div>
            ))}
            {data.total > data.items.length && (
              <p className="py-2 text-xs text-stone-500">e mais {data.total - data.items.length}…</p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Agora não</Button>
            <Button data-testid="prazos-dia-nao-util-confirmar" disabled={busy} onClick={fix} className="bg-stone-900 hover:bg-stone-800 text-white">
              {busy ? "Corrigindo…" : `Corrigir ${data.total === 1 ? "o prazo" : `os ${data.total} prazos`}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
